import React from 'react';
import { C } from '@parsina/framework/tokens';

import { useTranslation } from '../../i18n';

/**
 * «① مشخصات کلینیک ── ② نقش‌های کلینیک» — where you are in a short flow.
 *
 * The registration wizard has always had two steps and never said so. The
 * second step announced itself with «گام ۲ از ۲» in the header subtitle, which
 * is the one moment the information is no longer useful: by then you have
 * already committed. A first-time clinic owner filling in the first screen
 * could not tell whether they were one form from working or ten.
 *
 * Rendered as an ordered list, because that is what it is — `<ol>` with
 * `aria-current="step"` on the live one, so a screen reader hears «۲ of ۲»
 * without the visual metaphor. The strip is a plain flex row that inherits the
 * document direction, so step 1 starts where the eye starts in Persian, Arabic
 * and English alike.
 *
 * Digits go through `formatNumber`: «۱» in Persian, «1» in English, from one
 * source. A literal Persian digit in source is what the i18n ratchet exists to
 * catch.
 *
 * ── The look is the doctor panel's ─────────────────────────────────────────
 *
 * Since 2026-09-19 this draws exactly what the doctor's onboarding
 * `ProgressBar` draws (doctor-app src/screens/Onboarding): 32px discs — the
 * live one a rounded square in navy-dark, a finished one green with a check,
 * an upcoming one slate — labels UNDER the discs at 11px, and a 3px connector
 * of 32–60px between them, centred. Because the labels sit under the discs a
 * two-step strip fits a 320px phone, so the old one-line phone reduction is
 * gone: the phone shows the same stepper the doctor's phone shows.
 *
 * PROMOTION NOTE (packages/DESIGN.md rule 4): the kit has no stepper. When a
 * second app needs one — the doctor app's visit-closure flow is the likely
 * first — this moves to `packages/ui` unchanged; it carries no clinic
 * vocabulary and every label is a prop.
 */
export default function StepIndicator({ steps = [], current, completed, ariaLabel }) {
  const { formatNumber } = useTranslation();
  const done = completed instanceof Set ? completed : new Set(completed || []);

  return (
    <ol
      data-testid="step-indicator"
      aria-label={ariaLabel}
      style={{
        listStyle: 'none', margin: '0 0 28px', padding: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      {steps.map((step, i) => {
        const isDone = done.has(step.key);
        const isCurrent = !isDone && step.key === current;
        return (
          <React.Fragment key={step.key}>
            <li
              aria-current={step.key === current ? 'step' : undefined}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, minWidth: 0 }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 32, height: 32, borderRadius: isCurrent ? 8 : '50%',
                  background: isDone ? C.green : isCurrent ? C.navyDark : '#e2e8f0',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.3s',
                }}
              >
                {isDone ? (
                  <svg viewBox="0 0 24 24" fill="white" width="16" height="16"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" /></svg>
                ) : (
                  <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: isCurrent ? 'white' : C.textMuted }}>{formatNumber(i + 1)}</span>
                )}
              </span>
              <span
                title={step.label}
                style={{
                  fontSize: '0.6875rem',
                  color: isCurrent ? C.navyDark : isDone ? C.green : C.textMuted,
                  fontWeight: isCurrent ? 700 : 500,
                  whiteSpace: 'nowrap',
                }}
              >
                {step.label}
              </span>
            </li>
            {i < steps.length - 1 ? (
              <li
                role="presentation"
                aria-hidden="true"
                style={{
                  flex: 1, height: 3, minWidth: 32, maxWidth: 60, marginBottom: 18,
                  background: isDone ? C.green : '#e2e8f0',
                  transition: 'background 0.3s',
                }}
              />
            ) : null}
          </React.Fragment>
        );
      })}
    </ol>
  );
}
